/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Currency from '@xanadu/ui-shared/components/Currency';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';
import { roundDownSmallValue } from '@xanadu/ui-shared/utils/numbers';

import IconBonus from '../../../../../images/bonus/bonus-icon.svg';
import IconAccount from '../../../../../images/icons/misc/icon-account-2.svg';
import IconAlertWhite from '../../../../../images/icons/misc/icon-alert_white.svg';

import DotLoader from '../../DotLoader';
import BonusDisplay from '../BonusDisplay';

import styles from './Wallet.module.scss';
import WalletVariant2 from './WalletVariant2';

const Wallet = ({
  balance,
  balanceLoading,
  bonusAmount,
  bonusFreeFunds,
  freeSpins,
  freeBets,
  showBonusIcon,
  walletVariant,
  isMobile,
  cashierEnabled,
  continueGBGFVCCheck,
  loadingNetDeposit,
  netDepositError,
  hasEKYCErrorMessage,
  depositButtonText,
  depositButtonDisabled,
  onDepositClick,
  clientString,
  username,
  exposureLabel = 'Exposure',
  balanceLabel = 'Available',
  t,
}) => {
  const [balanceState, setBalanceState] = useState({
    label: balanceLabel,
    value: balance.freeFunds,
  });

  useEffect(() => {
    setBalanceState({ label: balanceLabel, value: balance.freeFunds });

    if (!isMobile || !bonusFreeFunds) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setBalanceState((prev) => {
        if (prev.label === 'Bonuses') {
          return { label: balanceLabel, value: balance.freeFunds };
        }
        return { label: 'Bonuses', value: bonusFreeFunds };
      });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isMobile, bonusFreeFunds, balanceLabel, balance.freeFunds]);

  const exposureClass = classNames({
    [styles.balanceContainer]: true,
    [styles['balanceContainer--exposure']]: true,
  });

  const balanceClass = classNames({
    [styles.balanceContainer]: true,
    [styles['balanceContainer--available']]: true,
  });

  const iconLoadingClass = classNames({
    [styles.icon]: true,
    [styles['icon--loading']]: true,
  });

  const depositButtonClass = classNames({
    [styles.button]: true,
    [styles.depositButton]: true,
  });

  const bonusIconClass = classNames({
    [styles.bonusToggle]: true,
    '.gtm_cta': true,
  });

  const BonusIconLink = () => (
    <Link className={bonusIconClass} to="/promotions" data-hook="casino-bonus-icon">
      <IconBonus className="bonus-toggle-icon" />
    </Link>
  );

  const depositButton = (
    <Button
      inverted
      className={depositButtonClass}
      sizeType="regular"
      buttonType="flat"
      dataHook="header-deposit"
      onClick={onDepositClick}
      disabled={depositButtonDisabled || loadingNetDeposit}
    >
      {t(depositButtonText)}
    </Button>
  );

  const accountLink = (
    <Link to={'/account'} className={styles.myAccountLink}>
      <IconAccount />
    </Link>
  );

  if (cashierEnabled && !isMobile && continueGBGFVCCheck && netDepositError) {
    return (
      <Fragment>
        <div className={styles.balancesGroup}>
          <div className={styles.icon}>
            <IconAlertWhite />
          </div>
          <div className={styles.balanceContainer}>
            {hasEKYCErrorMessage ? (
              <React.Fragment>
                <div className={styles.balanceTopText}>{t('Further Verification Needed')}</div>
                <div className={styles.balanceBottomText}>{t('Please upload documents')}</div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div className={styles.balanceTopText}>{t('Something went wrong')}</div>
                <div className={styles.balanceBottomText}>
                  {t('Unable to complete verification')}
                </div>
              </React.Fragment>
            )}
          </div>
          {depositButton}
        </div>
        {accountLink}
      </Fragment>
    );
  }

  if (cashierEnabled && !isMobile && continueGBGFVCCheck && loadingNetDeposit) {
    return (
      <Fragment>
        <div className={styles.balancesGroup}>
          <div className={iconLoadingClass}>
            <Spinner size="medium" theme="light" className={styles.loadingIcon} />
          </div>
          <div className={styles.balanceContainer}>
            <div className={styles.balanceTopText}>{t('Please wait a moment')}</div>
          </div>
          {depositButton}
        </div>
        {accountLink}
      </Fragment>
    );
  }

  if (cashierEnabled && !isMobile && continueGBGFVCCheck) {
    return (
      <Fragment>
        <div className={styles.balancesGroup}>
          <div className={styles.balanceContainer}>
            <div className={styles.balanceTopText}>
              {t('Welcome to')} {clientString}
            </div>
            <div className={styles.balanceBottomText}>{t('Deposit now to get started')}</div>
          </div>
          {depositButton}
        </div>
        {accountLink}
      </Fragment>
    );
  }

  if (walletVariant === 'v2') {
    return (
      <WalletVariant2
        username={username}
        balance={balance}
        balanceLoading={balanceLoading}
        bonusAmount={bonusAmount}
        bonusFreeFunds={bonusFreeFunds}
        freeSpins={freeSpins}
        freeBets={freeBets}
        showBonusIcon={showBonusIcon}
        isMobile={isMobile}
        cashierEnabled={cashierEnabled}
        loadingNetDeposit={loadingNetDeposit}
        depositButtonText={depositButtonText}
        depositButtonDisabled={depositButtonDisabled}
        onDepositClick={onDepositClick}
        exposureLabel={exposureLabel}
        balanceLabel={balanceLabel}
        t={t}
      />
    );
  }

  return (
    <div className={styles.main}>
      {!isMobile && showBonusIcon && <BonusIconLink />}
      <div className={exposureClass}>
        {balanceLoading ? (
          <DotLoader dark small />
        ) : (
          <React.Fragment>
            <div className={styles.balanceTitle}>{t(exposureLabel)}:</div>
            <div className={styles.balanceValue}>
              <Currency value={roundDownSmallValue(balance.exposure)} round="floor" />
            </div>
          </React.Fragment>
        )}
      </div>
      <Link to={'/account'} className={styles.myAccountLink}>
        <div className={balanceClass}>
          {balanceLoading ? (
            <DotLoader dark small />
          ) : (
            <React.Fragment>
              <div className={styles.balanceTitle}>{t(balanceState.label)}:</div>
              <div className={styles.balanceValue}>
                <Currency value={roundDownSmallValue(balanceState.value)} round="floor" />
              </div>
            </React.Fragment>
          )}
        </div>
      </Link>
      {cashierEnabled && !isMobile && (
        <BonusDisplay
          bonusAmount={bonusAmount}
          bonusFreeFunds={bonusFreeFunds}
          freeSpins={freeSpins}
          freeBets={freeBets}
          balanceLoading={balanceLoading}
          t={t}
        />
      )}
      {cashierEnabled && !isMobile && depositButton}
      {accountLink}
    </div>
  );
};

Wallet.propTypes = {
  balance: PropTypes.object.isRequired,
  balanceLoading: PropTypes.bool.isRequired,
  bonusAmount: PropTypes.number,
  bonusFreeFunds: PropTypes.number,
  freeSpins: PropTypes.string,
  freeBets: PropTypes.object,
  showBonusIcon: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  cashierEnabled: PropTypes.bool,
  continueGBGFVCCheck: PropTypes.bool,
  loadingNetDeposit: PropTypes.bool,
  netDepositError: PropTypes.string,
  hasEKYCErrorMessage: PropTypes.bool,
  depositButtonText: PropTypes.string.isRequired,
  depositButtonDisabled: PropTypes.bool,
  onDepositClick: PropTypes.func.isRequired,
  clientString: PropTypes.string,
  walletVariant: PropTypes.string,
  t: PropTypes.func.isRequired,
  exposureLabel: PropTypes.string,
  balanceLabel: PropTypes.string,
  username: PropTypes.string,
};

Wallet.defaultProps = {
  bonusAmount: 0,
  bonusFreeFunds: 0,
  freeSpins: '',
  freeBets: null,
  showBonusIcon: false,
  cashierEnabled: false,
  continueGBGFVCCheck: false,
  loadingNetDeposit: false,
  netDepositError: '',
  hasEKYCErrorMessage: false,
  depositButtonDisabled: false,
  clientString: '',
};

export default Wallet;
